import React, { useState } from "react";
import { FiLoader, FiX, FiInfo, FiZap } from "react-icons/fi";
import { card, labelClass, Pills, Slider } from "./VideoStudioPanel";
import ComparePreview from "./upscale/ComparePreview";
import VideoDropZone from "./videofx/VideoDropZone";
import ResultsList from "./videofx/ResultsList";
import useVideoFx from "./videofx/useVideoFx";
import { supported } from "../video/upscale/gl";
import { upscaleVideo } from "../video/upscale/process";
import { LONG_VIDEO_SECONDS, LOOKS, LOOK_IDS, clockText, estimateBytes, problemWith, settingsOf, sizeText, targetOptions } from "../video/upscale/plan";
import { recordableFormats } from "../video/timeline";

// AI Video Upscaling: make a video sharper and bigger (up to 4K), with optional clean-up of grain and compression blocks.
// Everything happens in the visitor's browser, on their graphics card: the video is never uploaded.
export default function VideoUpscalerTool() {
  const fx = useVideoFx();
  const { file, opened, fps, busy } = fx;
  const [formats] = useState(() => recordableFormats());
  const [format, setFormat] = useState(0);
  const [targetId, setTargetId] = useState("");
  const [look, setLook] = useState("natural");
  const [custom, setCustom] = useState({ sharp: LOOKS.custom.sharp, denoise: LOOKS.custom.denoise });

  const webgl = supported();
  const targets = opened ? targetOptions(opened.w, opened.h) : [];
  const target = targets.find((t) => t.id === targetId) || targets[targets.length > 1 ? 1 : 0];
  const settings = settingsOf(look, custom);
  const problem = opened && target ? problemWith({ w: target.w, h: target.h, seconds: opened.seconds, fps: fps || 30, bytes: file?.size || 0 }) : "";

  const start = () => {
    if (!opened || !target || problem || !formats.length) return;
    fx.run(async ({ signal, onProgress, onCanvas }) => ({
      result: await upscaleVideo({ file, size: [target.w, target.h], look: settings, mime: formats[format].mime, fps: fps || 30, onProgress, onCanvas, signal }),
      ext: formats[format].ext, look: LOOKS[look].label,
    }));
  };

  const sizeOnDisk = target && opened ? estimateBytes(target.w, target.h, fps || 30, opened.seconds) : 0;
  const longVideo = opened && opened.seconds > LONG_VIDEO_SECONDS;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Video Upscaling</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Make a video bigger and sharper - up to 4K. Each frame is enlarged with a high-quality Lanczos scaler, cleaned of grain and compression blocks if you like, and sharpened where it matters,
        without the halos around edges. It runs on your own graphics card: your video is never uploaded, and it is free.
      </p>

      {!webgl && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6" role="alert">This browser cannot use the graphics card for video (WebGL 2 is switched off). Use a current Chrome, Edge or Firefox.</p>}
      {webgl && !formats.length && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-4 mb-6" role="alert">This browser cannot save videos. Use a current Chrome, Edge, Firefox or Safari.</p>}

      {!opened ? <VideoDropZone reading={fx.reading} onFile={(f) => { fx.take(f); setTargetId(""); }} /> : (
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 items-start">
          <div className={card}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <span className="text-sm text-gray-200 truncate" title={file.name}>{file.name} <span className="text-gray-500">· {opened.w}×{opened.h} · {clockText(opened.seconds)}{fps ? ` · ${fps} fps` : ""} · {sizeText(file.size)}</span></span>
              <button type="button" onClick={fx.clear} disabled={!!busy} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1 shrink-0 disabled:opacity-40"><FiX size={13} /> Another video</button>
            </div>
            {busy ? (
              <div>
                <div ref={fx.liveHost} role="img" aria-label="The video being upscaled" className="rounded-xl bg-black min-h-[200px]" />
                <p className="text-xs text-gray-500 mt-2 text-center">This is the picture being made, frame by frame.</p>
              </div>
            ) : target && <ComparePreview key={opened.url} opened={opened} out={target} look={settings} />}
          </div>

          <div className="space-y-4 lg:sticky lg:top-24">
            <div className={`${card} space-y-6`}>
              <div>
                <span className={labelClass}>1. Make it</span>
                <Pills label="Size" value={target ? target.id : ""} onChange={setTargetId} options={targets.map((t) => [t.id, t.id === "same" ? "Same size" : `${t.label}`])} />
                {target && <p className="text-xs text-gray-400 mt-2.5">{opened.w}×{opened.h} → <strong className="text-gray-200">{target.w}×{target.h}</strong>{target.factor > 1 ? ` (${target.factor.toFixed(2).replace(/\.?0+$/, "")}× bigger)` : " (cleaned up, not enlarged)"}</p>}
                {targets.length === 1 && <p className="text-xs text-amber-200 mt-2">This video is already 4K, the biggest size offered. You can still clean it up and sharpen it.</p>}
              </div>
              <div>
                <span className={labelClass}>2. The look</span>
                <Pills label="Look" value={look} onChange={setLook} options={LOOK_IDS.map((k) => [k, k === "restore" ? "Restore" : LOOKS[k].label])} />
                <p className="text-xs text-gray-400 mt-2.5">{LOOKS[look].about}</p>
                {look === "custom" && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <Slider label="Sharpness" value={Math.round(custom.sharp * 100)} min={0} max={100} step={5} suffix="%" onChange={(v) => setCustom((c) => ({ ...c, sharp: v / 100 }))} />
                    <Slider label="Noise clean-up" value={Math.round(custom.denoise * 100)} min={0} max={100} step={5} suffix="%" onChange={(v) => setCustom((c) => ({ ...c, denoise: v / 100 }))} />
                  </div>
                )}
              </div>
              {formats.length > 1 && (
                <div>
                  <span className={labelClass}>3. File type</span>
                  <Pills label="File type" value={String(format)} onChange={(v) => setFormat(Number(v))} options={formats.map((f, i) => [String(i), f.label])} />
                </div>
              )}
            </div>

            <div className={card}>
              {problem && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4" role="alert">{problem}</p>}
              {busy ? (
                <div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-brand-gradient transition-all" style={{ width: `${Math.round(busy.progress * 100)}%` }} /></div>
                  <p className="text-sm text-gray-200 mt-3 flex items-center gap-2"><FiLoader className="animate-spin text-accent-light" /> Upscaling... {Math.round(busy.progress * 100)}% · {clockText(busy.progress * opened.seconds)} of {clockText(opened.seconds)}</p>
                  <p className="text-xs text-gray-500 mt-1.5">Keep this tab open and visible: browsers can only record a video while it plays.</p>
                  <button type="button" onClick={() => { fx.cancelRef.current.cancelled = true; }} className="mt-4 px-5 py-2.5 rounded-full border border-white/20 hover:border-red-400 text-sm font-semibold">Stop</button>
                </div>
              ) : (
                <>
                  <button type="button" onClick={start} disabled={!webgl || !formats.length || !!problem || !target}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
                    <FiZap /> Upscale the video{target && target.factor > 1 ? ` to ${target.h >= target.w ? target.w : target.h}p` : ""}
                  </button>
                  <ul className="text-xs text-gray-400 mt-4 space-y-1.5">
                    <li>It takes about as long as the video: <strong className="text-gray-200">{clockText(opened.seconds)}</strong>{opened.seconds > 20 ? "" : " (a short clip is quick)"}.</li>
                    <li>The new file will be about <strong className="text-gray-200">{sizeText(sizeOnDisk)}</strong>. Your sound is kept.</li>
                    {longVideo && <li className="text-amber-200">This is a long video. It is better to process it in parts.</li>}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {fx.error && <p className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6 flex items-start gap-3" role="alert"><FiInfo className="mt-0.5 shrink-0" /> {fx.error}</p>}

      <ResultsList results={fx.results} onRemove={fx.removeResult} suffix={(item) => `${item.h >= item.w ? item.w : item.h}p-upscaled`} />

      <div className="mt-10 grid md:grid-cols-3 gap-4 text-sm text-gray-400">
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">What it does</p>Enlarges every frame with a Lanczos scaler, sharpens edges adaptively and holds back halos. Real detail that is in the video comes out crisper; it does not invent detail that was never recorded.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Private and free</p>The video is processed by your graphics card and never leaves your computer. There is no limit on how many videos you make.</div>
        <div className="rounded-xl bg-white/5 border border-navy-border p-4"><p className="font-semibold text-gray-200 mb-1">Good to know</p>The result is recorded while the video plays, so it takes as long as the video. Use a recent Chrome, Edge or Firefox with the tab in front. Videos with copy protection cannot be processed.</div>
      </div>
    </div>
  );
}
